import { __decorate, __metadata, __param } from "tslib";
/**
 * 主要用于维护的用户的操作记录，提供Undo和Redo的功能
 */
import _ from 'lodash';
import { HsTableServiceSymbol } from '@/services/HsTableService';
import { Toast } from 'patsnap-biz';
import { UserUndoRedoServiceSymbol, userUndoRedoIds } from './UserUndoRedoService';
import { rvTrack } from 'track';
import cellsConfig from '@/presentation/components/folders/excelCells/cells.config';
import advanceSortService from 'library-@/components/advanceSort/service';
import patentField from '@/infrastructure/utils/patentField';
import { inject, provideTransient } from '@/inversify.config';
import { i18nSymbol } from '@/i18n';
const oppositeActionMap = {
    FREEZE: 'UNFREEZE',
    UNFREEZE: 'FREEZE',
    HIDE: 'SHOW',
    SHOW: 'HIDE',
};
export function transformFieldToColumn(field, isFree = false) {
    const { field_id, field_name, field_type, edit_value, width } = field;
    const sorted = advanceSortService.getFieldSortedInfo(field_id);
    const fieldStructure = advanceSortService.getFieldKeyFieldStructure(field_id);
    const sortOptions = _.map((fieldStructure || {}).options, (key) => {
        return advanceSortService.getSortKeyDirection(key);
    });
    const sortConfig = {
        fieldStructure,
        sorted,
        sortOptions: sortOptions && sortOptions.length ? sortOptions : null,
    };
    // 自定义字段
    if (field.is_custom) {
        return {
            _title: field_name,
            _fieldType: field_type,
            _props: field,
            _isCustom: true,
            data: field_id,
            readOnly: !edit_value || isFree,
            renderer: `${field_type}.render`,
            editor: `${field_type}.editor`,
            width: width || _.get(cellsConfig, `C_${field_type}.width`),
            _sortConfig: sortConfig,
        };
    }
    // 评论字段
    if (field_id === 'COMMENT') {
        return {
            _title: patentField(field_id),
            _props: field,
            data: field_id,
            width: width || _.get(cellsConfig, `${field_id}.width`),
            readOnly: isFree,
            editor: 'COMMENT.editor',
            renderer: 'COMMENT.render',
            _sortConfig: sortConfig,
        };
    }
    // 专利字段
    return {
        _title: patentField(field_id), // 区别于title
        _props: field,
        data: field_id,
        width: width || _.get(cellsConfig, `${field_id}.width`),
        readOnly: true,
        renderer: _.get(cellsConfig, `${field_id}.render`, 'DEFAULT.render'),
        _sortConfig: sortConfig,
    };
}
export const ExcelGridUndoRedoServiceSymbol = Symbol.for('ExcelGridUndoRedoService');
let ExcelGridUndoRedoService = class ExcelGridUndoRedoService {
    constructor(userUndoRedoService, hsTableService, i18n) {
        this.userUndoRedoService = userUndoRedoService;
        this.hsTableService = hsTableService;
        this.i18n = i18n;
        this.hsTableSettings = {};
        this.fieldsStructure = {};
        this.isFree = false;
    }
    init({ hsTableSettings, fieldsStructure, isFree }) {
        this.hsTableSettings = hsTableSettings;
        this.fieldsStructure = fieldsStructure;
        this.isFree = isFree;
    }
    addTaskForCustomFieldsIndex({ action, data }) {
        try {
            /**
             * data的格式为：[[0, 2, 1, 3], [0, 3, 1, 2]
             * 其中第一个元素为row行号，第二元素为字段id，三个元素为初始值（初始值可能为空），第四个元素为更新值
             * 将数组data中的第三个元素与第四个元素交换位置，可以得到Undo的数据
             * */
            const oldData = [];
            const newData = [];
            /**
             * 因为初始值可能为空，所以需要增加默认值，默认值需要根据更新值的类型来判断
             * 如果更新值为数组，则默认值为[],否则默认值为''
             * */
            let defaultValue = '';
            if (Array.isArray(data[0][3])) {
                defaultValue = [];
            }
            _.forEach(data, (item, index) => {
                // 值相同，或者都为空时
                if (_.isEqual(item[2], item[3]) || (!item[2] && !item[3])) {
                    return;
                }
                const _oldData = [...item];
                const temp = _oldData[2] || defaultValue;
                _oldData[2] = _oldData[3] || defaultValue;
                _oldData[3] = temp;
                const _rowData = this.hsTableService.hsInstance.getSourceDataAtRow(_oldData[0]);
                const rowData = {
                    PATENT_ID: _rowData?.PATENT_ID,
                };
                oldData.push({
                    data: _oldData,
                    rowData,
                });
                const _newData = [...item];
                newData.push({
                    data: _newData,
                    rowData,
                });
            });
            if (oldData.length === 0 && newData.length === 0) {
                return;
            }
            this.userUndoRedoService.addTask({
                id: userUndoRedoIds.CustomFieldsUpdateIndex,
                oldValue: {
                    action: action,
                    data: oldData,
                },
                newValue: {
                    action,
                    data: newData,
                },
            });
        }
        catch (error) {
            console.log('addTask error: ', error);
        }
    }
    addTaskForFieldSetting({ action, data }) {
        try {
            this.userUndoRedoService.addTask({
                id: userUndoRedoIds.HandsonTableFieldSetting,
                oldValue: {
                    action: this.getOldActionByNewAction(action),
                    data: this.formatDataToList({ action, data, isOld: true }),
                },
                newValue: {
                    action,
                    data: this.formatDataToList({ action, data }),
                },
            });
        }
        catch (error) {
            console.log('addTask error: ', error);
        }
    }
    updateCustomFieldsIndex({ action, data }, taskUuid) {
        if (!this.hsTableService.hsInstance)
            return;
        try {
            const _data = [];
            _.forEach(data, (item, index) => {
                const indexData = item.data;
                const rowData = item.rowData;
                // indexData[0], indexData[1], indexData[3]为值
                // 先根据数据索引检查当前数据是否还存在，如果不存在，使用PatentID查找需要更新的行
                const _rowPatentID = _.get(this.hsTableSettings, `data.${indexData[0]}.PATENT_ID`);
                let targetRowIndex = -1;
                if (_rowPatentID === _.get(rowData, 'PATENT_ID')) {
                    targetRowIndex = indexData[0];
                }
                else {
                    targetRowIndex = _.findIndex(this.hsTableSettings.data, { PATENT_ID: rowData.PATENT_ID });
                }
                if (targetRowIndex > -1) {
                    this.hsTableService.hsInstance.setDataAtRowProp(targetRowIndex, indexData[1], indexData[3]);
                    indexData[0] = targetRowIndex; // 当前Row的索引
                    _data.push(indexData);
                }
            });
            /**
             * selectCellData[0]是行号，selectCellData[1]是列的property
             * 如果是多条数据的索引撤回，默认选中第一条数据所在的单元格
             * */
            const selectCellData = _data[0];
            if (_.isNumber(selectCellData[0]) && !_.isUndefined(selectCellData[1])) {
                this.hsTableService.hsInstance.selectCell(selectCellData[0], selectCellData[1]);
            }
            return _data;
        }
        catch (error) {
            this.userUndoRedoService.removeTaskByUuid(taskUuid);
            rvTrack('Execute Undo Item Failed', {
                operation: action,
            });
        }
    }
    updateHandsonTableBySetting({ action, data }) {
        if (!this.hsTableService.hsInstance)
            return;
        // MOVE，需要补偿操作的index
        const dataLen = data.length;
        const settingDataList = [];
        try {
            const _data = _.map(data, (item, itemIndex) => {
                const index = this.hsTableService.hsInstance.propToCol(item.fields[0]);
                if (action === 'RESIZE') {
                    // 多个列RESIZE操作需要单独记录每一列的宽度，因此数据结构需要调整
                    const plugin = this.hsTableService.hsInstance.getPlugin('manualColumnResize');
                    plugin.setManualSize(index, item.width);
                    const _itemData = { ...item };
                    settingDataList.push(_itemData);
                }
                else if (action === 'FREEZE') {
                    const manualColumnFreezePlugin = this.hsTableService.hsInstance.getPlugin('manualColumnFreeze');
                    manualColumnFreezePlugin.freezeColumn(index);
                }
                else if (action === 'UNFREEZE') {
                    const manualColumnFreezePlugin = this.hsTableService.hsInstance.getPlugin('manualColumnFreeze');
                    manualColumnFreezePlugin.unfreezeColumn(index);
                }
                else if (action === 'MOVE') {
                    const manualColumnMovePlugin = this.hsTableService.hsInstance.getPlugin('manualColumnMove');
                    // MOVE操作，从前往后移动时，需要补偿自身的占位数
                    const toIndex = item.index > index ? item.index + (dataLen - itemIndex) : item.index;
                    manualColumnMovePlugin.moveColumn(index, toIndex);
                }
                else if (action === 'HIDE') {
                    // 显示隐藏后端接口暂未实现
                    const columns = [...this.hsTableSettings.columns];
                    // 在columns中删除列,删除位置为item.index
                    columns.splice(item.index, 1);
                    this.hsTableSettings.columns = columns;
                    this.updateColumnsAfterDataChange({ action, item });
                }
                else if (action === 'SHOW') {
                    const field = item.fields[0];
                    const fieldData = _.get(this.fieldsStructure, field, {});
                    fieldData.hide = false;
                    const columnData = transformFieldToColumn(fieldData, this.isFree);
                    const columns = [...this.hsTableSettings.columns];
                    // 在columns中插入新的列,插入位置为item.index,数据为columnData
                    columns.splice(item.index, 0, columnData);
                    this.hsTableSettings.columns = columns;
                    this.updateColumnsAfterDataChange({ action, item });
                }
                const _item = { ...item };
                if (_.isNumber(item.index)) {
                    _item.to_index = item.index > index ? item.index + (dataLen - itemIndex) : item.index;
                }
                delete _item.index;
                return _item;
            });
            if (action !== 'RESIZE') {
                const _settingData = this.formatListToData(_data);
                settingDataList.push(_settingData);
            }
            return settingDataList;
        }
        catch (error) {
            console.log(error);
        }
    }
    getOldActionByNewAction(action) {
        return oppositeActionMap[action] ? oppositeActionMap[action] : action;
    }
    formatDataToList({ action, data, isOld = false }) {
        const itemList = _.map(data.fields, (item, ItmIndex) => {
            // 旧数据，需要从 fieldsStructure 中取出原始数据
            const itemData = {
                fields: [item],
            };
            if (action === 'RESIZE') {
                const originalData = isOld ? _.get(this.fieldsStructure, item, {}) : data;
                // 旧数据，需要从 fieldsStructure 中取出原始数据，新数据，需要从 data 中获取
                // 如果原始数据中没有 width，需要从 cellsConfig 中取出默认值
                // 如果cellsConfig中没有 width，设置默认值为 200
                if (!originalData.width) {
                    originalData.width = (originalData.is_custom
                        ? _.get(cellsConfig, `C_${originalData.field_type}.width`)
                        : _.get(cellsConfig, `${originalData.field}.width`)) || 200;
                    console.log('originalData.width: ', originalData.width);
                }
                itemData.width = originalData.width;
            }
            if ((action === 'FREEZE' || action === 'UNFREEZE')) {
                const index = this.hsTableService.hsInstance.propToCol(item);
                itemData.index = isOld ? index : data.to_index;
            }
            if (action === 'MOVE') {
                const index = this.hsTableService.hsInstance.propToCol(item);
                // 多个Item一起移动时，需要加上ItmIndex
                if (isOld) {
                    itemData.index = index;
                }
                else {
                    if (index > data.to_index) {
                        itemData.index = _.toNumber(data.to_index + ItmIndex);
                    }
                    else {
                        // 从前往后移动时，Index需要减去ItmIndex
                        itemData.index = _.toNumber(data.to_index + ItmIndex) - data.fields.length;
                    }
                }
            }
            if (action === 'SHOW' || action === 'HIDE') {
                const index = this.hsTableService.hsInstance.propToCol(item);
                itemData.index = index;
            }
            return itemData;
        });
        return itemList;
    }
    formatListToData(itemList) {
        const data = {
            fields: [],
            to_index: null,
        };
        _.forEach(itemList, (item) => {
            data.fields = [...data.fields, ...item.fields];
            if (_.isNil(data.to_index) && item.to_index !== null) {
                data.to_index = item.to_index;
            }
        });
        return data;
    }
    updateColumnsAfterDataChange({ action, item }) {
        const index = item.index;
        const fixedColumnsLeft = this.hsTableService.hsInstance.getSettings().fixedColumnsLeft;
        if (index < fixedColumnsLeft) {
            if (action === 'SHOW') {
                this.hsTableSettings.fixedColumnsLeft = fixedColumnsLeft + 1;
            }
            else if (action === 'HIDE') {
                this.hsTableSettings.fixedColumnsLeft = fixedColumnsLeft - 1;
            }
        }
    }
    showUndoRedoToast(action) {
        Toast({
            message: this.i18n.t(`undoAction.undoSuccessTips.${action}`),
            duration: 2000,
            type: 'success',
        });
    }
};
ExcelGridUndoRedoService = __decorate([
    provideTransient(ExcelGridUndoRedoServiceSymbol),
    __param(0, inject(UserUndoRedoServiceSymbol)),
    __param(1, inject(HsTableServiceSymbol)),
    __param(2, inject(i18nSymbol)),
    __metadata("design:paramtypes", [Object, Object, Object])
], ExcelGridUndoRedoService);
export { ExcelGridUndoRedoService };
